import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { PORTFOLIO_PROJECTS } from '@/lib/data';
import { ArrowUpRight, X, CheckCircle2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
type Project = typeof PORTFOLIO_PROJECTS[0];
export function Portfolio() {
  const [filter, setFilter] = useState('Tous');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const categories = ['Tous', ...new Set(PORTFOLIO_PROJECTS.map(p => p.category))];
  const filteredProjects = filter === 'Tous'
    ? PORTFOLIO_PROJECTS
    : PORTFOLIO_PROJECTS.filter(p => p.category === filter);
  return (
    <section id="portfolio" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-brand-lime font-bold tracking-widest uppercase text-sm mb-4">Réalisations</h2>
            <h3 className="text-4xl md:text-5xl font-display font-bold text-brand-purple mb-8">Projets qui inspirent</h3>
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setFilter(cat)}
                  className={cn(
                    "px-6 py-2 rounded-full text-sm font-semibold transition-all border",
                    filter === cat
                      ? "bg-brand-purple text-white border-brand-purple"
                      : "bg-white text-brand-purple/60 border-brand-purple/10 hover:border-brand-purple/30"
                  )}
                >
                  {cat}
                </button>
              ))}
            </div>
          </motion.div>
        </div>
        <motion.div
          layout
          className="grid md:grid-cols-2 gap-8"
        >
          <AnimatePresence mode='popLayout'>
            {filteredProjects.map((item) => (
              <motion.div
                layout
                key={item.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.4 }}
                className="group relative rounded-3xl overflow-hidden cursor-pointer shadow-lg border border-gray-100"
                onClick={() => setSelectedProject(item)}
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={item.img}
                    alt={item.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-brand-purple via-brand-purple/20 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500 flex flex-col justify-end p-8">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-brand-lime text-sm font-bold">{item.category}</span>
                    <div className="w-10 h-10 rounded-full bg-brand-lime text-brand-purple flex items-center justify-center group-hover:rotate-45 transition-transform">
                      <ArrowUpRight className="w-5 h-5" />
                    </div>
                  </div>
                  <h4 className="text-white text-3xl font-display font-bold mb-2">{item.title}</h4>
                  <p className="text-white/80 text-sm line-clamp-2 mb-4">{item.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {item.tags.map(tag => (
                      <span key={tag} className="text-[10px] px-2 py-1 rounded bg-white/10 text-white border border-white/20 uppercase tracking-widest">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
        {/* Project Detail Modal */}
        <Dialog open={!!selectedProject} onOpenChange={() => setSelectedProject(null)}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0 border-none bg-white rounded-3xl">
            {selectedProject && (
              <div className="flex flex-col">
                <div className="h-64 sm:h-96 relative">
                  <img 
                    src={selectedProject.img} 
                    alt={selectedProject.title} 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-brand-purple/40" />
                  <div className="absolute bottom-8 left-8 text-white z-10">
                    <span className="text-brand-lime font-bold uppercase tracking-wider text-xs">{selectedProject.category}</span>
                    <h2 className="text-4xl font-display font-bold mt-2">{selectedProject.title}</h2>
                  </div>
                  <button 
                    onClick={() => setSelectedProject(null)}
                    className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/40 backdrop-blur-md rounded-full flex items-center justify-center text-white transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                <div className="p-8 sm:p-12 space-y-12">
                  <section>
                    <h3 className="text-2xl font-bold text-brand-purple mb-4">Vue d'ensemble</h3>
                    <p className="text-brand-purple/70 text-lg leading-relaxed">
                      {selectedProject.fullDescription}
                    </p>
                  </section>
                  <div className="grid md:grid-cols-2 gap-12">
                    <section className="bg-brand-purple/5 p-6 rounded-2xl border border-brand-purple/10">
                      <h4 className="text-xl font-bold text-brand-purple mb-4 flex items-center gap-2">
                         <div className="w-8 h-8 rounded-full bg-brand-purple text-white flex items-center justify-center text-xs">01</div>
                         Le Challenge
                      </h4>
                      <p className="text-brand-purple/80">{selectedProject.challenge}</p>
                    </section>
                    <section className="bg-brand-lime/10 p-6 rounded-2xl border border-brand-lime/20">
                      <h4 className="text-xl font-bold text-brand-purple mb-4 flex items-center gap-2">
                         <div className="w-8 h-8 rounded-full bg-brand-lime text-brand-purple flex items-center justify-center text-xs">02</div>
                         Notre Solution
                      </h4>
                      <p className="text-brand-purple/80">{selectedProject.solution}</p>
                    </section>
                  </div>
                  <section>
                    <h3 className="text-2xl font-bold text-brand-purple mb-6">Résultats Clés</h3>
                    <div className="flex items-start gap-4 p-6 bg-white border border-gray-100 rounded-2xl shadow-sm">
                      <CheckCircle2 className="w-8 h-8 text-green-500 shrink-0" />
                      <div>
                        <p className="text-xl font-medium text-brand-purple leading-snug">
                          {selectedProject.results}
                        </p>
                      </div>
                    </div>
                  </section>
                  <div className="flex flex-wrap gap-3 pt-8 border-t border-gray-100">
                    {selectedProject.tags.map(tag => (
                      <span key={tag} className="px-4 py-1.5 rounded-full bg-gray-100 text-brand-purple/60 text-xs font-bold uppercase tracking-widest">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
}